/** Shared vehicle inspection angles — keep in sync with backend `required_inspection_angles`. */

export const THIRD_ROW_ANGLE = "interior_3rd_row";
export const THIRD_ROW_MIN_SEATS = 7;

const ANGLE_DEFS = {
  front: { id: "front", label: "Front", hint: "Full front bumper and hood" },
  rear: { id: "rear", label: "Rear", hint: "Full rear bumper and trunk" },
  driver_side: { id: "driver_side", label: "Driver side", hint: "Full left side profile" },
  passenger_side: { id: "passenger_side", label: "Passenger side", hint: "Full right side profile" },
  interior: { id: "interior", label: "Interior", hint: "Dashboard and front seats" },
  interior_2nd_row: {
    id: "interior_2nd_row",
    label: "2nd row",
    hint: "Rear passenger seats / cabin",
  },
  interior_3rd_row: {
    id: "interior_3rd_row",
    label: "3rd row",
    hint: "Third-row seats (7+ seat vehicles)",
  },
  boot: { id: "boot", label: "Boot / trunk", hint: "Open trunk or cargo area" },
  odometer: { id: "odometer", label: "Odometer", hint: "Clear odometer reading in km" },
};

/** Always-required angle ids (excludes conditional 3rd row). */
export const BASE_INSPECTION_ANGLE_IDS = [
  "front",
  "rear",
  "driver_side",
  "passenger_side",
  "interior",
  "interior_2nd_row",
  "boot",
  "odometer",
];

export function coerceSeats(seats) {
  const n = parseInt(seats, 10);
  return Number.isFinite(n) ? n : 0;
}

/** Resolve angle defs from API `angles_required` ids (preferred). */
export function resolveInspectionAngles(ids) {
  if (!Array.isArray(ids) || !ids.length) return inspectionAnglesForSeats();
  return ids.map((id) => ANGLE_DEFS[id] || { id, label: id.replace(/_/g, " "), hint: "" });
}

/** BE-aligned required set: base angles + 3rd row when seats >= 7. */
export function inspectionAnglesForSeats(seats) {
  const ids = [...BASE_INSPECTION_ANGLE_IDS];
  if (coerceSeats(seats) >= THIRD_ROW_MIN_SEATS) {
    const idx = ids.indexOf("interior_2nd_row") + 1;
    ids.splice(idx, 0, THIRD_ROW_ANGLE);
  }
  return resolveInspectionAngles(ids);
}

/**
 * Prefer API angles_required; otherwise derive from seats.
 * @param {{ anglesRequired?: string[], seats?: number|string }} opts
 */
export function inspectionAnglesForContext({ anglesRequired, seats } = {}) {
  if (Array.isArray(anglesRequired) && anglesRequired.length) {
    return resolveInspectionAngles(anglesRequired);
  }
  return inspectionAnglesForSeats(seats);
}

export function inspectionPhotosComplete(captured, angles) {
  const list = angles?.length ? angles : inspectionAnglesForSeats();
  return list.every((a) => captured?.[a.id]?.file_id);
}

export function inspectionPhotosPayload(captured, angles) {
  const list = angles?.length ? angles : inspectionAnglesForSeats();
  return list.map((a) => captured?.[a.id]).filter(Boolean).map((p) => ({
    angle: p.angle,
    file_id: p.file_id,
  }));
}

export function inspectionAngleCountLabel(count) {
  const n = Number(count) || 0;
  return `${n} angle${n === 1 ? "" : "s"}`;
}
